import type { ESearchPredicateTypes } from '@common/definitions';
import type { Predicate } from '@common/types/objects';

export const PROMOTION_TYPES = {
  BAR: 'bar',
  BADGE: 'badge',
} as const;

export type PromotionType = typeof PROMOTION_TYPES[keyof typeof PROMOTION_TYPES];

/** Marketing promo chip styles for CDN badges / bars. */
export const PROMOTION_VARIANTS = {
  NEW: 'new',
  AI: 'ai',
  COMING_SOON: 'coming-soon',
  MAINTENANCE: 'maintenance',
  ANNOUNCEMENT: 'announcement',
  BETA: 'beta',
  UPDATED: 'updated',
  LIMITED: 'limited',
} as const;

export type PromotionPromoVariant = typeof PROMOTION_VARIANTS[keyof typeof PROMOTION_VARIANTS];

/** shadcn entity status variants — valid in badges.json for `type: "badge"`. */
export const PROMOTION_STATUS_VARIANTS = {
  LIVE: 'live',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  ARCHIVED: 'archived',
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  DELETED: 'deleted',
} as const;

export type PromotionStatusVariant = typeof PROMOTION_STATUS_VARIANTS[keyof typeof PROMOTION_STATUS_VARIANTS];

export type PromotionVariant = PromotionPromoVariant | PromotionStatusVariant;

export const PROMOTION_PLACEMENTS = {
  TOP_LEFT: 'top-left',
  TOP: 'top',
  TOP_RIGHT: 'top-right',
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM: 'bottom',
  BOTTOM_RIGHT: 'bottom-right',
} as const;

export type PromotionPlacement = typeof PROMOTION_PLACEMENTS[keyof typeof PROMOTION_PLACEMENTS];

export const PROMOTION_ANIMATIONS = {
  NONE: 'none',
  PULSE: 'pulse',
  SHIMMER: 'shimmer',
  BOUNCE: 'bounce',
} as const;

export type PromotionAnimation = typeof PROMOTION_ANIMATIONS[keyof typeof PROMOTION_ANIMATIONS];

export const PROMOTION_LABEL_EFFECTS = {
  NONE: 'none',
  GRADIENT_PRIMARY: 'gradient-primary',
  GRADIENT_AI: 'gradient-ai',
} as const;

export type PromotionLabelEffect = typeof PROMOTION_LABEL_EFFECTS[keyof typeof PROMOTION_LABEL_EFFECTS];

export const PROMOTION_SIZES = {
  XS: 'xs',
  SM: 'sm',
} as const;

export type PromotionSize = typeof PROMOTION_SIZES[keyof typeof PROMOTION_SIZES];

export const PROMOTION_OFFSET_CLAMP = 40;

export interface PromotionBadgeOffset {
  x?: number;
  y?: number;
}

export interface PromotionCta {
  label: string;
  href: string;
  /** When omitted, `http(s)://` hrefs open in a new tab; app paths use client-side routing. */
  external?: boolean;
}

export interface PromotionAudience {
  predicate_type: ESearchPredicateTypes;
  predicates: Predicate[];
}

export interface PromotionEntry {
  key: string;
  type: PromotionType;
  label?: string;
  message?: string;
  cta?: PromotionCta;
  variant: PromotionVariant;
  enabled: boolean;
  startAt: string;
  endAt: string;
  dismissible?: boolean;
  priority?: number;
  routes?: string[];
  excludedRoutes?: string[];
  audience?: PromotionAudience;
  /** Badge chip position on the host. Defaults to `top-right`. See PROMOTION_PLACEMENTS. */
  placement?: PromotionPlacement;
  /** Fine-tune position in px on top of semantic placement. Clamped to ±PROMOTION_OFFSET_CLAMP. */
  offset?: PromotionBadgeOffset;
  /** Preset motion. Defaults to `none`. Disabled when prefers-reduced-motion. */
  animation?: PromotionAnimation;
  /** Optional text/border effect on top of shadcn badge variant. Defaults to `none`. */
  labelEffect?: PromotionLabelEffect;
  /** Chip scale. Defaults to `xs`. */
  size?: PromotionSize;
  /** Extra classes on the positioning wrapper (`.DynamicBadge`). */
  className?: string;
  /** Extra classes on the badge chip (`.DynamicBadge__chip`). */
  chipClassName?: string;
  /** Extra classes on the label text inside the chip. */
  labelClassName?: string;
}

export interface PromotionsState {
  items: PromotionEntry[];
  loaded: boolean;
  dismissedKeys: string[];
}

export const PROMOTIONS_DISMISSED_STORAGE_KEY = 'promotions_dismissed';

export const PROMOTIONS_SET = 'promotions/SET';
export const PROMOTIONS_DISMISS = 'promotions/DISMISS';

export interface PromotionsSetAction {
  type: typeof PROMOTIONS_SET;
  items: PromotionEntry[];
}

export interface PromotionsDismissAction {
  type: typeof PROMOTIONS_DISMISS;
  key: string;
}

export type PromotionsAction = PromotionsSetAction | PromotionsDismissAction;
